import { ref, computed } from 'vue';
import * as Tone from 'tone';
import type { SoundType, TimeSignature } from '../types';
import { useStorage } from '@vueuse/core';
import type { Instrument, InstrumentOptions } from 'tone/build/esm/instrument/Instrument';
import { isCompoundTime, roundToTwoDecimals } from '@/utils';
import { MAX_BPM, METRONOME_LOOKAHEAD_SECOND, SPIN_ANIMATION_DURATION, TEMPO_RANGES } from '@/constants';
import { useGlobalAudioContext } from './useGlobalAudioContext';

const globalAudio = useGlobalAudioContext();
Tone.setContext(globalAudio.getAudioContext());

const isPlaying = ref(false);
const offsetTime = ref(0);
const currentBeat = ref(0);
const currentBeatVisual = ref(0);
let visialUpdateTimeout = setTimeout(()=>{});
const storedTimeSignature = useStorage<TimeSignature>('metronome-time-signature', { numerator: 4, denominator: 4 });
const timeSignature = computed({
  get: () => storedTimeSignature.value,
  set: (newTimeSignature: TimeSignature) => {
    if (loop) {
      loop.stop();
      loop.dispose();
    }
    storedTimeSignature.value = newTimeSignature;
    currentBeat.value = 0;
    currentBeatVisual.value = 0;
    loop = getLoop(sound);
    loop.start(0);
  }
})
const timeSignatureOptions = useStorage<TimeSignature[]>('metronome-time-signature-options', [
  { numerator: 3, denominator: 4 },
  { numerator: 4, denominator: 4 },
  { numerator: 6, denominator: 8 },
  { numerator: 12, denominator: 8 },
]);
const storedSoundType = useStorage<SoundType>('metronome-sound', 'ableton');
const storedTempo = useStorage('metronome-tempo', 120);
const tempo = computed({
  get: () => storedTempo.value,
  set: (newTempo: number) => {
    storedTempo.value = newTempo;
    Tone.getTransport().bpm.value = newTempo;
    isStartingOrUpdatingTempo = true;
    if (navigator.mediaSession.metadata) {
      navigator.mediaSession.metadata.title = `${newTempo} BPM in ${timeSignature.value.numerator}/${timeSignature.value.denominator}`;
    }
  }
});
let sound: Instrument<InstrumentOptions> = createNormalSynth("triangle");
let loop = getLoop(sound);
let firstNodeJSTime: number;
const numOfBeats = ref(0);
let isStartingOrUpdatingTempo = false;
const isSpinning = ref(false);

const accentFirstBeat = useStorage('metronome-accent-first-beat', true);
const quarterNoteVolume = useStorage('metronome-quarter-note-volume', 1);
const eighthNoteVolume = useStorage('metronome-eighth-note-volume', 1);
const sixteenthNoteVolume = useStorage('metronome-sixteenth-note-volume', 0);
const quarterNoteMuted = useStorage('metronome-quarter-note-muted', false);
const eighthNoteMuted = useStorage('metronome-eighth-note-muted', false);
const sixteenthNoteMuted = useStorage('metronome-sixteenth-note-muted', false);

async function triggerAttackRelease(note: string, duration: string) {
  sound.triggerAttackRelease(note, duration)
}

function createNormalSynth(soundType: OscillatorType) {
  try {
    return new Tone.Synth({
      oscillator: { type: soundType },
      envelope: { attack: 0.001, decay: 0.1, sustain: 0, release: 0.1 }
    }).toDestination();
  } catch {
    return createNormalSynth("triangle");
  }
};

function createWoodblockSynth() {
  return new Tone.Sampler({
    urls: {
      "C5 ": "woodblock.mp3",
    },
    baseUrl: "/sounds/wood/",
    onload: () => {
      console.log("Woodblock sample loaded");
    }
  }).toDestination();
};

function createPluckSynth() {
  return new Tone.PluckSynth({
    attackNoise: 1,
    dampening: 4000,
    resonance: 0.7
  }).toDestination();
};

function createClaveSound() {
  return new Tone.Sampler({
    urls: {
      "C5 ": "clave.wav",
    },
    baseUrl: "/sounds/wood/",
    onload: () => {
      console.log("clave sample loaded");
    }
  }).toDestination();
}

function createHardWoodSynth() {
  return new Tone.Sampler({
    urls: {
      "C5 ": "hardwood.wav",
    },
    baseUrl: "/sounds/wood/",
    onload: () => {
      console.log("hardwood sample loaded");
    }
  }).toDestination();
}


function createPingSynth() {
  return new Tone.Sampler({
    urls: {
      "C5": "ping-hi.wav",
      "G4": "ping-low.wav",
    },
    baseUrl: "/sounds/wood/",
    onload: () => {
      console.log("ping samples loaded");
    }
  }).toDestination();
}

function createLogicSound() {
  return new Tone.Sampler({
    urls: {
      "C5": "MetronomeUp.wav",
      "G4": "Metronome.wav",
    },
    baseUrl: "/sounds/DAWs/Logic/",
    onload: () => {
      console.log("Logic Pro samples loaded");
    }
  }).toDestination();
}

function createMaschineSound() {
  return new Tone.Sampler({
    urls: {
      "C5": "MetronomeUp.wav",
      "G4": "Metronome.wav",
    },
    baseUrl: "/sounds/DAWs/Maschine/",
    onload: () => {
      console.log("Maschine samples loaded");
    }
  }).toDestination();
}

function createMPCSound() {
  return new Tone.Sampler({
    urls: {
      "C5": "MetronomeUp.wav",
      "G4": "Metronome.wav",
    },
    baseUrl: "/sounds/DAWs/MPC/",
    onload: () => {
      console.log("MPC samples loaded");
    }
  }).toDestination();
}

function createAbletonSound() {
  return new Tone.Sampler({
    urls: {
      "C5": "MetronomeUp.wav",
      "G4": "Metronome.wav",
    },
    baseUrl: "/sounds/DAWs/Ableton/",
    onload: () => {
      console.log("Ableton Live samples loaded");
    }
  }).toDestination();
}

function createFLStudioSound() {
  return new Tone.Sampler({
    urls: {
      "C5": "MetronomeUp.wav",
      "G4": "Metronome.wav",
    },
    baseUrl: "/sounds/DAWs/FL_Studio/",
    onload: () => {
      console.log("FL Studio samples loaded");
    }
  }).toDestination();
}

function createCubaseSound() {
  return new Tone.Sampler({
    urls: {
      "C5": "MetronomeUp.wav",
      "G4": "Metronome.wav",
    },
    baseUrl: "/sounds/DAWs/Cubase/",
    onload: () => {
      console.log("Cubase samples loaded");
    }
  }).toDestination();
}

function createProToolsDefaultSound() {
  return new Tone.Sampler({
    urls: {
      "C5": "MetronomeUp.wav",
      "G4": "Metronome.wav",
    },
    baseUrl: "/sounds/DAWs/Pro_Tools/Default/",
    onload: () => {
      console.log("Pro Tools default samples loaded");
    }
  }).toDestination();
}

function createProToolMarimbaSound() {
  return new Tone.Sampler({
    urls: {
      "C5": "MetronomeUp.wav",
      "G4": "Metronome.wav",
    },
    baseUrl: "/sounds/DAWs/Pro_Tools/Marimba/",
    onload: () => {
      console.log("Pro Tools marimba samples loaded");
    }
  }).toDestination();
}

function createReasonSound() {
  return new Tone.Sampler({
    urls: {
      "C5": "MetronomeUp.wav",
      "G4": "Metronome.wav",
    },
    baseUrl: "/sounds/DAWs/Reason/",
    onload: () => {
      console.log("reason samples loaded");
    }
  }).toDestination();
}

function createSonarSound() {
  return new Tone.Sampler({
    urls: {
      "C5": "MetronomeUp.wav",
      "G4": "Metronome.wav",
    },
    baseUrl: "/sounds/DAWs/Sonar/",
    onload: () => {
      console.log("sonar samples loaded");
    }
  }).toDestination();
}

function getLoop(_synth: Instrument<InstrumentOptions>) {
  return new Tone.Loop(async (time) => {
    const idealInterval = 60000/tempo.value;
    const bufferTime = (time - Tone.getContext().currentTime)*1000;
    let nextNoteJSTime = Date.now() + bufferTime + offsetTime.value;

    if (isStartingOrUpdatingTempo) {
      firstNodeJSTime = nextNoteJSTime;
      numOfBeats.value = 0;
    }

    let currentDelta = nextNoteJSTime - (firstNodeJSTime + numOfBeats.value*idealInterval);
 
    if (Math.abs(currentDelta) > 20){
      offsetTime.value -= currentDelta;
      nextNoteJSTime -= currentDelta;
    }

    if (!isStartingOrUpdatingTempo && time + offsetTime.value/1000 <= Tone.getContext().currentTime) {
      offsetTime.value += idealInterval;
      currentBeat.value = (currentBeat.value + 1) % timeSignature.value.numerator;
      currentBeatVisual.value = currentBeat.value;
      numOfBeats.value++;
      nextNoteJSTime += offsetTime.value;
      currentDelta = nextNoteJSTime - (firstNodeJSTime + numOfBeats.value*idealInterval);
    }
    // console.log({currentDelta}, {idealInterval}, {idealNextNoteJSTime: firstNodeJSTime + numOfBeats.value*idealInterval}, {nextNoteJSTime}, {time}, {offsetTime: offsetTime.value}, {currentTime: Tone.getContext().currentTime}, {lookAhead: Tone.getContext().lookAhead});

    const { numerator, denominator } = timeSignature.value;
    const isCompoundMeter = isCompoundTime(numerator);
    const beatDuration = isCompoundMeter ? 1.5 : 1;
    
    // Calculate beat position based on compound time
    const beatPosition = roundToTwoDecimals((currentBeat.value * 4 / denominator) % beatDuration);
    
    let volume = quarterNoteVolume.value;
    let isMuted = quarterNoteMuted.value;
    
    if (isCompoundMeter) {
      if (denominator === 8) {
        if (beatPosition === 0.5 || beatPosition === 1) {
          volume = eighthNoteVolume.value;
          isMuted = eighthNoteMuted.value;
        } else if (beatPosition === 0.25 || beatPosition === 0.75 || beatPosition === 1.25) {
          volume = sixteenthNoteVolume.value;
          isMuted = sixteenthNoteMuted.value;
        }
      } else if (denominator === 16) {
        if (beatPosition === 0.75) {
          volume = eighthNoteVolume.value;
          isMuted = eighthNoteMuted.value;
        } else if (beatPosition === 0.25 || beatPosition === 0.5 || beatPosition === 1 || beatPosition === 1.25) {
          volume = sixteenthNoteVolume.value;
          isMuted = sixteenthNoteMuted.value;
        }
      }
    } else {
      if (beatPosition === 0.5) {
        volume = eighthNoteVolume.value;
        isMuted = eighthNoteMuted.value;
      } else if (beatPosition !== 0) {
        volume = sixteenthNoteVolume.value;
        isMuted = sixteenthNoteMuted.value;
      }
    }

    const isAccent = accentFirstBeat.value && currentBeat.value === 0;
    _synth.triggerAttackRelease(isAccent ? "C5" : "G4", "32n", time + offsetTime.value/1000, isMuted ? 0 : volume);
    // try {
    //   _synth.triggerAttackRelease(isAccent ? "C5" : "G4", "32n", time + offsetTime.value/1000, isMuted ? 0 : volume);
    // } catch {
    //   console.log("Trigger before current audio context time!");
    //   console.log({currentDelta}, {idealInterval}, {idealNextNoteJSTime: firstNodeJSTime + numOfBeats.value*idealInterval}, {nextNoteJSTime}, {time}, {offsetTime: offsetTime.value}, {currentTime: Tone.getContext().currentTime}, {lookAhead: Tone.getContext().lookAhead});
    //   offsetTime.value += idealInterval;
    //   currentBeat.value = (currentBeat.value + 1) % timeSignature.value.numerator;
    //   numOfBeats.value++;
    //   nextNoteJSTime += offsetTime.value;
    //   currentDelta = nextNoteJSTime - (firstNodeJSTime + numOfBeats.value*idealInterval);
    // }

    currentBeat.value = (currentBeat.value + 1) % timeSignature.value.numerator;
    updateCurrentBeatVisual(currentBeat.value, (time + offsetTime.value/1000 - Tone.getContext().currentTime)*1000)
    numOfBeats.value++;

    if (isStartingOrUpdatingTempo) {
      isStartingOrUpdatingTempo = false;
    }
  }, "4n");
}

function updateCurrentBeatVisual(beat: number, time: number) {
  visialUpdateTimeout = setTimeout(()=>{
    currentBeatVisual.value = beat;
  },time);
}

export function useMetronome() {
  function updateSound(soundType: SoundType) {
    storedSoundType.value = soundType;
    if (loop) {
      loop.stop();
      loop.dispose();
    }
    if (sound) {
      sound.dispose();
    } 

    switch (soundType) {
      case 'woodblock':
        sound = createWoodblockSynth();
        break;
      case 'pluck':
        sound = createPluckSynth();
        break;
      case 'clave':
        sound = createClaveSound();
        break;
      case 'hardwood':
        sound = createHardWoodSynth();
        break;
      case 'ping':
        sound = createPingSynth();
        break;
      case 'ableton':
        sound = createAbletonSound();
        break;
      case 'cubase':
        sound = createCubaseSound();
        break;
      case 'fl-studio':
        sound = createFLStudioSound();
        break;
      case 'logic':
        sound = createLogicSound();
        break;
      case 'maschine':
        sound = createMaschineSound();
        break;
      case 'mpc':
        sound = createMPCSound();
        break;
      case 'protools default':
        sound = createProToolsDefaultSound();
        break;
      case 'protools marimba':
        sound = createProToolMarimbaSound();
        break;
      case 'reason':
        sound = createReasonSound();
        break;
      case 'sonar':
        sound = createSonarSound();
        break;
      default:
        sound = createNormalSynth(soundType as OscillatorType);
    }
    loop = getLoop(sound);
    loop.start(0);
  };

  function tick(isAccent: boolean, volume: number = 1) {
    const now = Tone.now();
    // on ios, at least need 0.01 (10ms)
    try {
      sound.triggerAttackRelease(isAccent ? "C5" : "G4", "32n", Tone.getContext().currentTime + METRONOME_LOOKAHEAD_SECOND, volume);
    } catch (e){
      console.error("Error triggering metronome tick:", e);
    }
  }

  async function start() {
    firstNodeJSTime = Date.now();
    isStartingOrUpdatingTempo = true;
    Tone.getTransport().bpm.value = tempo.value;
    if (!globalAudio.isAudioRunning()) {
      await globalAudio.ensureReadyFromUserGesture();
    }
    Tone.getTransport().start();
    isPlaying.value = true;
    // Tone.getContext().lookAhead = 0;
    navigator.mediaSession.playbackState = "playing";
  };

  function getLookAhead(){
    return Tone.getContext().lookAhead;
  }

  function setLookAhead(lookAhead: number){
    Tone.getContext().lookAhead = lookAhead;
  }

  async function stop() {
    clearTimeout(visialUpdateTimeout);
    Tone.getTransport().stop();
    // TODO: this is causing delay when replaying
    // if (Tone.getContext().state === "running") {
    //   await audioContext.suspend();
    // }
    isPlaying.value = false;
    currentBeat.value = 0;
    currentBeatVisual.value = 0;
    offsetTime.value = 0;
    navigator.mediaSession.playbackState = "paused";
  };

  const randomizeTempo = () => {
    const { denominator, numerator } = timeSignature.value;
    const range = TEMPO_RANGES[denominator as keyof typeof TEMPO_RANGES];
    
    if (!range) return;
    
    let min = range.min as number;
    let max = range.max as number;
    
    // Adjust for compound time
    if (isCompoundTime(numerator)) {
      min = Math.min(MAX_BPM, Math.round(min * 1.5));
      max = Math.min(MAX_BPM, Math.round(max * 1.5));
    }
    
    const newTempo = Math.floor(Math.random() * (max - min + 1)) + min;
    tempo.value = newTempo;
  };

  const randomizeTimeSignature = () => {
    if (timeSignatureOptions.value.length === 0) return timeSignature.value;
    const randomIndex = Math.floor(Math.random() * timeSignatureOptions.value.length);
    const newTimeSignature = timeSignatureOptions.value[randomIndex];
    timeSignature.value = newTimeSignature;
    return newTimeSignature;
  };

  return {
    isPlaying,
    isSpinning,
    tempo,
    timeSignature,
    currentBeat,
    currentBeatVisual,
    accentFirstBeat,
    timeSignatureOptions,
    tick,
    start,
    stop,
    randomizeTempo,
    randomizeTimeSignature,
    updateSound,
    triggerAttackRelease,
    getLookAhead,
    setLookAhead,
    SPIN_ANIMATION_DURATION,
    quarterNoteVolume,
    eighthNoteVolume,
    sixteenthNoteVolume,
    quarterNoteMuted,
    eighthNoteMuted,
    sixteenthNoteMuted,
    numOfBeats,
  };
} 
